import { useRef, useState } from "react";
import LoanApplicationModal from "../../shared/LoanApplicationModal";
import CustomInput from "../../shared/CustomInput";
import { LocateIcon, Upload } from "lucide-react";
import { uploadImage } from "../../sdk/upload/upload";

const ResidenceLocation = ({
  closeResidenceLocation,
  activeIndex,
  handleNextStep,
  borrowerResidenceInfo,
  setBorrowerResidenceInfo,
  loading,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const fileInputRef = useRef(null);

  const handleChooseFileClick = () => fileInputRef.current.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const response = await uploadImage(file);
      setBorrowerResidenceInfo({
        ...borrowerResidenceInfo,
        residenceImageUrl: response.url,
      });
      setSelectedFile(file);
    }
  };

  const getLocation = () => {
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setBorrowerResidenceInfo({
          ...borrowerResidenceInfo,
          residenceLatitude: position.coords.latitude,
          residenceLongitude: position.coords.longitude,
        });
        setShowCoordinates(true);
        setFetchingLocation(false);
      },
      (error) => {
        console.error("Error getting location", error);
        setFetchingLocation(false);
      },
    );
  };

  return (
    <LoanApplicationModal
      closeBioInformation={closeResidenceLocation}
      activeIndex={activeIndex}
      handleNextStep={handleNextStep}
      loading={loading}
    >
      <p className="text-[#000000] font-normal text-[14px]">
        Create a New Loan Application
      </p>
      <p className="text-[#000000] font-semibold text-[20px]">
        Borrower’s Residence Location
      </p>
      <p className="text-[#5C5C5C] text-[14px] font-normal">
        Enter borrower’s residential location details
      </p>

      {/* Button to get current location */}
      {!showCoordinates && (
        <div className="my-4">
          <button
            onClick={getLocation}
            className="text-[#fff] bg-[#000000] text-[14px] h-[35px] flex items-center justify-center gap-[10px] rounded-[30px] px-[20px] font-medium"
            disabled={fetchingLocation}
          >
            <LocateIcon size={16} color="#fff" />
            {fetchingLocation
              ? "Fetching location..."
              : "Use my current location"}
          </button>
        </div>
      )}

      {/* Latitude & Longitude Inputs */}
      {showCoordinates && (
        <div className="flex items-center gap-[20px] my-[10px]">
          <div className="flex w-[45%] flex-col">
            <label className="text-[#222222] font-medium text-[13px]">
              Residence Latitude
            </label>
            <CustomInput
              placeholder="Latitude"
              type="number"
              value={borrowerResidenceInfo?.residenceLatitude || ""}
              onChange={(e) =>
                setBorrowerResidenceInfo({
                  ...borrowerResidenceInfo,
                  residenceLatitude: e.target.value,
                })
              }
            />
          </div>
          <div className="flex w-[45%] flex-col">
            <label className="text-[#222222] font-medium text-[13px]">
              Residence Longitude
            </label>
            <CustomInput
              placeholder="Longitude"
              type="number"
              value={borrowerResidenceInfo?.residenceLongitude || ""}
              onChange={(e) =>
                setBorrowerResidenceInfo({
                  ...borrowerResidenceInfo,
                  residenceLongitude: e.target.value,
                })
              }
            />
          </div>
        </div>
      )}

      {/* Location Link & Physical Address */}
      <div className="flex items-center gap-[20px] my-[10px]">
        <div className="flex w-[45%] flex-col">
          <label className="text-[#222222] font-medium text-[13px]">
            Location Link
          </label>
          <CustomInput
            placeholder="Enter location link"
            type="text"
            value={borrowerResidenceInfo?.residenceImageUrl}
            onChange={(e) =>
              setBorrowerResidenceInfo({
                ...borrowerResidenceInfo,
                residenceImageUrl: e.target.value,
              })
            }
          />
        </div>
        <div className="flex w-[45%] flex-col">
          <label className="text-[#222222] font-medium text-[13px]">
            Physical Address
          </label>
          <CustomInput
            placeholder="Enter physical address"
            type="text"
            value={borrowerResidenceInfo?.residencePhysicalAddress}
            onChange={(e) =>
              setBorrowerResidenceInfo({
                ...borrowerResidenceInfo,
                residencePhysicalAddress: e.target.value,
              })
            }
          />
        </div>
      </div>

      {/* Location Description */}
      <div className="flex items-center gap-[20px] my-[10px]">
        <div className="flex w-[45%] flex-col">
          <label className="text-[#222222] font-medium text-[13px]">
            Location Description
          </label>
          <CustomInput
            placeholder="Enter location description"
            type="text"
            value={borrowerResidenceInfo?.residenceLocationDescription}
            onChange={(e) =>
              setBorrowerResidenceInfo({
                ...borrowerResidenceInfo,
                residenceLocationDescription: e.target.value,
              })
            }
          />
        </div>
      </div>

      {/* File Upload */}
      <div className="flex w-[45%] my-[20px] flex-col">
        <p className="text-[#222222] font-medium text-[13px]">
          Residence Photo{" "}
          <span className="font-normal text-[#5C5C5C]">
            .jpeg or .png file only
          </span>
        </p>
        <div
          className="h-[90px] flex flex-col justify-center items-center gap-[10px] rounded-[8px] border-[0.7px] border-[#A8A8A8] bg-white cursor-pointer"
          onClick={handleChooseFileClick}
        >
          {selectedFile ? (
            <div className="text-center text-[12px] text-black">
              <p className="font-semibold">{selectedFile.name}</p>
              <p className="text-gray-500">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
            </div>
          ) : (
            <>
              <Upload />
              <p className="text-[#000000] text-[12px] font-normal">
                Drag and Drop file here or{" "}
                <span className="text-[#000000] underline">Choose file</span>{" "}
                here
              </p>
            </>
          )}
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
    </LoanApplicationModal>
  );
};

export default ResidenceLocation;
